import React, { useState, useEffect, useCallback } from 'react';
import './mso.css';

import { getMsoData, submitMsoSignoff, regenerateMso } from '../../lib/api';

/**
 * Manager Sign Off (MSO) tab content.
 *
 * Renders inside DocumentDetail as the "Manager Sign Off" tab.
 * Compares Usmon Screenshots vs. Tech Report content and lets an admin sign off.
 *
 * Props:
 *   caseId    string  - the PID/case identifier
 *   isAdmin   boolean - controls whether signoff actions are enabled
 *   onChange  func    - optional, fired when signoff state changes (lets parent refresh)
 */
export default function MSOTab({ caseId, isAdmin = false, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomItem, setZoomItem] = useState(null);
  const [acknowledged, setAcknowledged] = useState(new Set());
  const [comments, setComments] = useState('');
  const [signoffPending, setSignoffPending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMsoData(caseId);
      setData(result);
      // Pre-fill acknowledgements + comments from prior signoff if present;
      // otherwise clear, so a regenerate doesn't carry stale state forward.
      if (result?.signoff?.items_acknowledged?.length) {
        setAcknowledged(new Set(result.signoff.items_acknowledged));
      } else {
        setAcknowledged(new Set());
      }
      setComments(result?.signoff?.comments || '');
    } catch (e) {
      setError(e.message || 'Failed to load MSO data');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleAck = useCallback((itemId) => {
    setAcknowledged(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const handleSignoff = useCallback(async (decision) => {
    if (!isAdmin || signoffPending) return;
    setSignoffPending(true);
    setError(null);
    try {
      await submitMsoSignoff(caseId, {
        decision,
        comments,
        items_acknowledged: [...acknowledged],
      });
      await loadData();
      if (onChange) onChange();
    } catch (e) {
      setError(`Signoff failed: ${e.message || e}`);
    } finally {
      setSignoffPending(false);
    }
  }, [isAdmin, signoffPending, caseId, comments, acknowledged, loadData, onChange]);

  const handleRegenerate = useCallback(async () => {
    if (!isAdmin || regenerating) return;
    setRegenerating(true);
    setError(null);
    try {
      await regenerateMso(caseId);
      await loadData();
    } catch (e) {
      setError(`Regenerate failed: ${e.message || e}`);
    } finally {
      setRegenerating(false);
    }
  }, [isAdmin, regenerating, caseId, loadData]);

  // Loading
  if (loading && !data) {
    return (
      <div className="mso-loading">
        <div className="mso-spinner" />
        <span>Loading comparison…</span>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="mso-error">
        <strong>Error:</strong> {error}
        <button className="mso-btn-link" onClick={loadData}>Retry</button>
      </div>
    );
  }

  // Pending — sources missing
  if (!data || data.status === 'pending') {
    return <EmptyState data={data} />;
  }

  // Processing — comparison running
  if (data.status === 'processing') {
    return (
      <div className="mso-loading">
        <div className="mso-spinner" />
        <span>Generating comparison…</span>
      </div>
    );
  }

  const { status, comparison, signoff } = data;
  const isSigned = status === 'approved' || status === 'rejected';

  return (
    <div className="mso-tab">
      <StatusStrip
        status={status}
        comparison={comparison}
        signoff={signoff}
        onRegenerate={handleRegenerate}
        canRegenerate={isAdmin && !isSigned}
        regenerating={regenerating}
      />

      {isSigned && (
        <SignoffHistory signoff={signoff} status={status} />
      )}

      {comparison?.unmatched_items?.length > 0 && (
        <DiscrepancyList
          items={comparison.unmatched_items}
          isAdmin={isAdmin && !isSigned}
          isSigned={isSigned}
          acknowledged={acknowledged}
          onToggleAck={handleToggleAck}
          onZoom={setZoomItem}
        />
      )}

      {comparison?.matched_items?.length > 0 && (
        <MatchedList
          items={comparison.matched_items}
          totalMatched={comparison.matched_count}
          onZoom={setZoomItem}
        />
      )}

      {!isSigned && isAdmin && comparison && (
        <SignoffPanel
          ackCount={acknowledged.size}
          totalDiscrepancies={comparison.unmatched_items?.length || 0}
          comments={comments}
          onCommentsChange={setComments}
          onApprove={() => handleSignoff('approved')}
          onReject={() => handleSignoff('rejected')}
          pending={signoffPending}
        />
      )}

      <ZoomModal item={zoomItem} onClose={() => setZoomItem(null)} />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function StatusStrip({ status, comparison, signoff, onRegenerate, canRegenerate, regenerating }) {
  const pillClass = `mso-pill mso-pill-${status}`;
  const pillLabel = {
    ready: 'Ready for Review',
    approved: 'Approved',
    rejected: 'Rejected — Returned for Correction',
  }[status] || status;

  const coverage = comparison?.coverage_percent ?? 0;
  const coverageClass = coverage >= 95 ? 'good' : 'warn';

  return (
    <div className="mso-card">
      <div className="mso-status-strip">
        <div className={pillClass}>
          <span className="dot" />
          {pillLabel}
        </div>
        <div className="mso-meta-grid">
          {status === 'ready' && comparison && (
            <>
              <Meta label="Generated" value={formatDate(comparison.generated_at)} mono />
              <Meta
                label="Method"
                value={`${capitalize(comparison.extraction_method)} · ${comparison.matched_count + (comparison.unmatched_items?.length || 0)} items`}
                mono
              />
            </>
          )}
          {(status === 'approved' || status === 'rejected') && signoff && (
            <>
              <Meta label={status === 'approved' ? 'Signed By' : 'Rejected By'} value={signoff.manager_email} mono />
              <Meta label={status === 'approved' ? 'Signed At' : 'Rejected At'} value={formatDate(signoff.signed_at)} mono />
            </>
          )}
          <Meta label="Coverage">
            <div className="mso-coverage">
              <div className="mso-coverage-track">
                <div className={`mso-coverage-fill ${coverageClass}`} style={{ width: `${coverage}%` }} />
              </div>
              <span className="mso-coverage-text">
                {comparison?.matched_count}/{comparison?.matched_count + (comparison?.unmatched_items?.length || 0)}
              </span>
            </div>
          </Meta>
        </div>
        {canRegenerate && (
          <button className="mso-btn mso-btn-regen" onClick={onRegenerate} disabled={regenerating}>
            <RegenIcon />
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono, children }) {
  return (
    <div>
      <div className="mso-meta-label">{label}</div>
      {children ?? <div className={`mso-meta-value ${mono ? 'mono' : ''}`}>{value}</div>}
    </div>
  );
}

function DiscrepancyList({ items, isAdmin, isSigned, acknowledged, onToggleAck, onZoom }) {
  return (
    <div className="mso-card">
      <div className="mso-section-header mso-section-danger">
        <div className="mso-section-title">
          {isSigned ? 'Acknowledged Discrepancies' : 'Discrepancies — Require Review'}
        </div>
        <div className="mso-section-count">{items.length}</div>
      </div>
      {items.map(item => (
        <DiscrepancyItem
          key={item.id}
          item={item}
          isAdmin={isAdmin}
          isSigned={isSigned}
          isAcknowledged={acknowledged.has(item.id)}
          onToggleAck={() => onToggleAck(item.id)}
          onZoom={() => onZoom(item)}
        />
      ))}
    </div>
  );
}

function DiscrepancyItem({ item, isAdmin, isSigned, isAcknowledged, onToggleAck, onZoom }) {
  const rowClass = `mso-item-row ${item.match_status === 'partial' ? 'partial' : 'unmatched'} ${isSigned ? 'signed' : ''}`;
  return (
    <div className={rowClass}>
      <div className="mso-item-icon">
        {isSigned ? <CheckIcon /> : (item.match_status === 'partial' ? <WarnIcon /> : <XIcon />)}
      </div>
      <div className="mso-item-main">
        <div className="mso-item-title-row">
          <span className="mso-item-modality">{item.modality}</span>
          <EventTag event={item.event_type} />
          {item.match_status === 'partial' && (
            <span className="mso-partial-tag">Partial Match</span>
          )}
        </div>
        <div className="mso-item-meta">
          Page {item.page} · {item.timestamp}
          {item.value && ` · ${item.value}`}
          {item.duration && ` · ${item.duration}`}
        </div>
        {!isSigned && (
          <EvidencePanels item={item} variant="discrepancy" onZoom={onZoom} />
        )}
      </div>
      <div className="mso-item-ack">
        <button
          type="button"
          className={`mso-ack-checkbox ${isAcknowledged ? 'checked' : ''} ${!isAdmin ? 'disabled' : ''}`}
          onClick={isAdmin ? onToggleAck : undefined}
          disabled={!isAdmin}
          aria-label="Acknowledge this discrepancy"
        />
      </div>
    </div>
  );
}

function MatchedList({ items, totalMatched, onZoom }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mso-card">
      <button
        className={`mso-matched-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span>
          <CheckIcon /> {totalMatched} Items Matched
        </span>
        <ChevronIcon className="chevron" />
      </button>
      {open && (
        <div className="mso-matched-list">
          {items.map(item => (
            <MatchedItem key={item.id} item={item} onZoom={onZoom} />
          ))}
          {totalMatched > items.length && (
            <div className="mso-matched-more">
              + {totalMatched - items.length} more matched items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MatchedItem({ item, onZoom }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className={`mso-matched-row ${open ? 'expanded' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="mso-matched-check">✓</span>
        <div>
          <span className="mso-item-modality">{item.modality}</span>
          <EventTag event={item.event_type} small />
          <div className="mso-item-meta">
            Page {item.page} · {item.timestamp}
            {item.value && ` · ${item.value}`}
          </div>
        </div>
        <span className="mso-matched-ref">{item.tech_report_ref}</span>
        <ChevronIcon className="row-chev" />
      </div>
      {open && (
        <div className="mso-matched-evidence">
          <EvidencePanels item={item} variant="matched" onZoom={onZoom} />
        </div>
      )}
    </>
  );
}

function EvidencePanels({ item, variant, onZoom }) {
  const ev = item.evidence || {};
  const panelClass = variant === 'matched' ? 'mso-evidence matched' : 'mso-evidence';
  return (
    <div className={panelClass}>
      <div className="mso-ev-panel">
        <div className="mso-ev-panel-header">
          <span className="mso-ev-panel-label">Screenshot Page {item.page}</span>
          <span className="mso-ev-panel-meta">{item.timestamp}</span>
        </div>
        <div className="mso-ev-screenshot" onClick={() => onZoom(item)}>
          {ev.screenshot_url ? (
            <img src={ev.screenshot_url} alt={`Screenshot page ${item.page}`} />
          ) : ev.screenshot_svg ? (
            <div dangerouslySetInnerHTML={{ __html: ev.screenshot_svg }} />
          ) : (
            <div className="mso-ev-placeholder">Screenshot unavailable</div>
          )}
          <div className="mso-ev-zoom-hint"><ZoomIcon /> Zoom</div>
        </div>
      </div>
      <TechReportPanel item={item} variant={variant} />
    </div>
  );
}

function TechReportPanel({ item, variant }) {
  const [showFull, setShowFull] = useState(false);
  const ev = item.evidence || {};
  const status = item.match_status || 'matched';

  let metaText = '';
  if (variant === 'matched') metaText = '✓ Match confirmed';
  else if (status === 'unmatched') metaText = ev.tech_report_excerpt ? 'No direct match' : 'No match found';
  else if (status === 'partial') metaText = 'Partial match';

  return (
    <div className="mso-ev-panel">
      <div className="mso-ev-panel-header">
        <span className="mso-ev-panel-label">Tech Report</span>
        <span className="mso-ev-panel-meta">{metaText}</span>
      </div>
      {status === 'unmatched' && !ev.tech_report_excerpt ? (
        <div className="mso-ev-not-found">
          <InfoIcon />
          <div>
            No matching event found in Tech Report.
            {ev.tech_report_closest && (
              <> Closest narrative: <em>"{ev.tech_report_closest}"</em></>
            )}
          </div>
        </div>
      ) : (
        <div className="mso-ev-text-body">
          <Highlighted text={ev.tech_report_excerpt} keyword={item.modality} />
        </div>
      )}
      {ev.tech_report_full_section && variant !== 'matched' && (
        <>
          <button
            className={`mso-ev-section-toggle ${showFull ? 'open' : ''}`}
            onClick={() => setShowFull(o => !o)}
          >
            <ChevronIcon className="chev" />
            {showFull ? 'Hide' : 'Show'} full {ev.tech_report_section_name || 'section'}
          </button>
          {showFull && (
            <div className="mso-ev-full-section">
              <div className="mso-ev-full-section-content">
                {ev.tech_report_full_section}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Highlighted({ text, keyword }) {
  if (!text) return null;
  if (!keyword) return <strong>"{text}"</strong>;
  // Basic highlight — splits text around the keyword (case-insensitive)
  const parts = text.split(new RegExp(`(${escapeRegex(keyword)})`, 'gi'));
  return (
    <>
      <strong>"{parts.map((p, i) =>
        p.toLowerCase() === keyword.toLowerCase()
          ? <mark key={i}>{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}"</strong>
    </>
  );
}

function SignoffPanel({ ackCount, totalDiscrepancies, comments, onCommentsChange, onApprove, onReject, pending }) {
  return (
    <div className="mso-signoff-panel">
      <div className="mso-signoff-header">
        <SignoffIcon />
        <span>Manager Sign Off</span>
      </div>
      <div className="mso-signoff-body">
        <div className="mso-ack-status">
          <span>Discrepancies acknowledged:</span>
          <strong>{ackCount} / {totalDiscrepancies}</strong>
        </div>
        <label className="mso-signoff-label">Comments (optional)</label>
        <textarea
          className="mso-signoff-textarea"
          placeholder="e.g. Verified with tech via phone — alert was a transient artifact, intentionally omitted from the report."
          value={comments}
          onChange={e => onCommentsChange(e.target.value)}
          disabled={pending}
        />
        <div className="mso-signoff-actions">
          <button className="mso-btn mso-btn-reject" onClick={onReject} disabled={pending}>
            <XIcon /> Reject
          </button>
          <button className="mso-btn mso-btn-approve" onClick={onApprove} disabled={pending}>
            <CheckIcon /> {pending ? 'Signing…' : 'Approve & Sign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SignoffHistory({ signoff, status }) {
  return (
    <div className="mso-card">
      <div className={`mso-section-header ${status === 'rejected' ? 'mso-section-danger' : ''}`}>
        <div className="mso-section-title">Sign-Off Record</div>
      </div>
      <div className="mso-history-row">
        <div className="mso-history-meta">
          <span className={`mso-history-decision ${status}`}>
            {status === 'approved' ? 'Approved' : 'Rejected'}
          </span>
          <span className="mso-history-who">{signoff.manager_email}</span>
          <span className="mso-history-when">{formatDate(signoff.signed_at)}</span>
        </div>
        {signoff.items_acknowledged && (
          <div className="mso-history-ack">
            {signoff.items_acknowledged.length} discrepancies acknowledged.
          </div>
        )}
        {signoff.comments && (
          <div className="mso-history-comment">"{signoff.comments}"</div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ data }) {
  const sources = data?.required_sources || [
    { name: 'Usmon Screenshots', present: false, detail: 'missing' },
    { name: 'Technical Report', present: false, detail: 'missing' },
  ];
  return (
    <div className="mso-card">
      <div className="mso-card-header">
        <div className="mso-card-icon"><InfoIcon /></div>
        <div>
          <div className="mso-card-title">Manager Sign Off</div>
          <div className="mso-card-subtitle">Comparison cannot run until both sources are uploaded</div>
        </div>
      </div>
      <div className="mso-empty">
        <DocIcon size={40} />
        <div className="mso-empty-title">Waiting on source files</div>
        <div className="mso-empty-msg">
          MSO comparison runs automatically once both Usmon Screenshots and the Technical Report are uploaded for this case.
        </div>
        <div className="mso-empty-checklist">
          {sources.map(src => (
            <div key={src.name}>
              <span className={src.present ? 'ck' : 'x'}>{src.present ? '✓' : '○'}</span>
              {src.name}
              {src.detail && <span className="mso-empty-detail"> ({src.detail})</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ZoomModal({ item, onClose }) {
  useEffect(() => {
    if (!item) return;
    const onEsc = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [item, onClose]);

  if (!item) return null;
  const ev = item.evidence || {};
  return (
    <div className="mso-zoom-modal" onClick={onClose}>
      <button className="mso-zoom-close" onClick={onClose} aria-label="Close zoom">×</button>
      <div className="mso-zoom-content" onClick={e => e.stopPropagation()}>
        {ev.screenshot_url ? (
          <img src={ev.screenshot_url} alt={`Screenshot page ${item.page}`} />
        ) : ev.screenshot_svg ? (
          <div dangerouslySetInnerHTML={{ __html: ev.screenshot_svg }} />
        ) : null}
      </div>
    </div>
  );
}

function EventTag({ event, small }) {
  const cls = `mso-event-tag ${event?.toLowerCase().replace(/\s+/g, '-')} ${small ? 'small' : ''}`;
  return <span className={cls}>{event}</span>;
}

/* ---------- Inline icons (no external lib) ---------- */

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const DocIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 9h6" /><path d="M9 13h6" /><path d="M9 17h4" />
  </svg>
);
const ChevronIcon = ({ className }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const RegenIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
  </svg>
);
const ZoomIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
    <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const SignoffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m9 11 3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

/* ---------- Helpers ---------- */

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 16).replace('T', ' ');
  } catch {
    return iso;
  }
}
function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : ''; }
function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }